import { useEffect, useRef } from 'react'
import { cn } from '@/lib/cn'

/**
 * Animated neural-network particle background - the auth-page backdrop.
 * Lives ONLY in AuthLayout; nothing else in the app should mount this.
 *
 * Rendering details:
 *  - Nodes pick from a small brand-aligned palette so the field has
 *    subtle colour variety instead of a single blue dot.
 *  - Each node has its own pulsing alpha/radius so the canvas feels alive
 *    even when perfectly still.
 *  - Links between nearby nodes fade with distance - closer pairs draw
 *    brighter strands, far pairs fade out.
 *  - Mouse position is tracked via `window.mousemove`, so hovering over
 *    the form still influences the background. Nodes gently drift toward
 *    the cursor and connections within a larger radius light up, giving
 *    the "reactive" effect without intercepting pointer events.
 */
export function NeuralCanvas({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const isDark = document.documentElement.getAttribute('data-theme') === 'dark'

    // Single brand-blue colour for the whole network - keeps the canvas
    // feeling calm and on-brand.
    const BLUE = isDark
      ? { r: 96, g: 165, b: 250 }  // blue-400
      : { r: 37, g: 99,  b: 235 } // blue-600

    type Node = {
      x: number
      y: number
      vx: number
      vy: number
      baseRadius: number
      pulseOffset: number
    }

    let nodes: Node[] = []
    let raf = 0
    const t0 = performance.now()
    const mouse = { x: -10000, y: -10000, active: false }

    const reset = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      // Denser than the original 60-node cap so the "network" actually
      // looks like a network. Scales with viewport area.
      const count = Math.min(110, Math.max(40, Math.floor((w * h) / 12000)))
      nodes = Array.from({ length: count }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        baseRadius: 1.4 + Math.random() * 1.6,
        pulseOffset: Math.random() * Math.PI * 2,
      }))
    }

    // Draw a link between every pair of nodes so the network truly reads
    // as "fully connected." Opacity falls off quadratically with distance
    // so far-apart links fade to invisible and near-neighbours stay crisp.
    const MAX_LINK_DIST = 420 // beyond this the pair is culled entirely
    const HOVER_RADIUS = 200  // nodes within this of the cursor light up

    const draw = (now: number) => {
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      ctx.clearRect(0, 0, w, h)

      const elapsed = (now - t0) / 1000

      // Motion update - reduced-motion users get static nodes but we still
      // render hover reactions.
      for (const n of nodes) {
        if (!prefersReduced) {
          n.x += n.vx
          n.y += n.vy
          if (n.x < 0 || n.x > w) n.vx *= -1
          if (n.y < 0 || n.y > h) n.vy *= -1

          // Gentle pull toward the cursor when it's active and close.
          if (mouse.active) {
            const dx = mouse.x - n.x
            const dy = mouse.y - n.y
            const d2 = dx * dx + dy * dy
            const pullRange = 200 * 200
            if (d2 < pullRange && d2 > 1) {
              const d = Math.sqrt(d2)
              const force = (1 - d / 200) * 0.08
              n.vx += (dx / d) * force
              n.vy += (dy / d) * force
            }
          }
          // Soft damping - allows a click impulse to punch through
          // momentarily, then the mesh settles back to the calm 0.9 cap.
          const speed = Math.hypot(n.vx, n.vy)
          if (speed > 0.9) {
            // Scale down gradually instead of hard-capping so scatter
            // impulses stay visible.
            const damp = 0.94
            n.vx *= damp
            n.vy *= damp
          }
        }
      }

      // Fully-connected graph: draw every pair, but fade opacity with
      // distance so only near-neighbours show as solid strands while
      // distant pairs become hair-thin ghosts that still suggest the
      // "every node knows every other node" mesh.
      const maxSq = MAX_LINK_DIST * MAX_LINK_DIST
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]!
          const b = nodes[j]!
          const dx = a.x - b.x
          const dy = a.y - b.y
          const d2 = dx * dx + dy * dy
          if (d2 > maxSq) continue

          const d = Math.sqrt(d2)
          // Quadratic falloff - nearby links read clearly, far ones fade
          // almost to nothing.
          const falloff = Math.pow(1 - d / MAX_LINK_DIST, 2)

          // Boost links near the cursor so the field visibly reacts to
          // hover.
          let hoverBoost = 0
          if (mouse.active) {
            const mx = (a.x + b.x) / 2 - mouse.x
            const my = (a.y + b.y) / 2 - mouse.y
            const md = Math.hypot(mx, my)
            if (md < HOVER_RADIUS) hoverBoost = 1 - md / HOVER_RADIUS
          }

          const baseAlpha = isDark ? 0.32 : 0.26
          const alpha = Math.min(
            0.6,
            baseAlpha * falloff + hoverBoost * 0.35,
          )
          if (alpha < 0.02) continue

          ctx.strokeStyle = `rgba(${BLUE.r},${BLUE.g},${BLUE.b},${alpha})`
          ctx.lineWidth = 1 + hoverBoost * 0.8
          ctx.beginPath()
          ctx.moveTo(a.x, a.y)
          ctx.lineTo(b.x, b.y)
          ctx.stroke()
        }
      }

      // Nodes with a soft glow. Using a radial gradient on each is costly
      // at high counts, so we draw a halo ring + a solid core for the
      // same visual effect at a fraction of the price.
      for (const n of nodes) {
        const pulse = prefersReduced
          ? 0
          : Math.sin(elapsed * 1.8 + n.pulseOffset) * 0.35
        const r = Math.max(0.8, n.baseRadius + pulse * 0.6)

        // Cursor proximity: nodes near the cursor brighten.
        let glow = 0
        if (mouse.active) {
          const dx = n.x - mouse.x
          const dy = n.y - mouse.y
          const md = Math.hypot(dx, dy)
          if (md < HOVER_RADIUS) glow = 1 - md / HOVER_RADIUS
        }

        // Outer halo
        ctx.fillStyle = `rgba(${BLUE.r},${BLUE.g},${BLUE.b},${(isDark ? 0.18 : 0.13) + glow * 0.25})`
        ctx.beginPath()
        ctx.arc(n.x, n.y, r * (2.6 + glow * 1.2), 0, Math.PI * 2)
        ctx.fill()

        // Bright core
        ctx.fillStyle = `rgba(${BLUE.r},${BLUE.g},${BLUE.b},${(isDark ? 0.85 : 0.7) + glow * 0.25})`
        ctx.beginPath()
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2)
        ctx.fill()
      }

      raf = requestAnimationFrame(draw)
    }

    reset()
    raf = requestAnimationFrame(draw)

    const onResize = () => reset()
    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouse.x = e.clientX - rect.left
      mouse.y = e.clientY - rect.top
      mouse.active = true
    }
    const onMouseLeave = () => {
      mouse.active = false
    }

    // Click anywhere → scatter the nodes outward from the click point.
    // Nodes within the blast radius pick up a radial impulse that decays
    // naturally via the velocity cap in the draw loop.
    const onClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const BLAST_RADIUS = 260
      for (const n of nodes) {
        const dx = n.x - cx
        const dy = n.y - cy
        const d = Math.hypot(dx, dy) || 0.001
        if (d > BLAST_RADIUS) continue
        const strength = (1 - d / BLAST_RADIUS) * 6
        n.vx += (dx / d) * strength
        n.vy += (dy / d) * strength
      }
    }

    window.addEventListener('resize', onResize)
    window.addEventListener('mousemove', onMouseMove, { passive: true })
    window.addEventListener('mouseleave', onMouseLeave)
    window.addEventListener('blur', onMouseLeave)
    window.addEventListener('click', onClick)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseleave', onMouseLeave)
      window.removeEventListener('blur', onMouseLeave)
      window.removeEventListener('click', onClick)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 h-full w-full bg-auth-gradient',
        className,
      )}
    />
  )
}
